import { Button, DatePicker, Select } from '@scuf/common';
import Header from '@scuf/common/dist/components/Modal/Header/Header';
import { DataTable } from '@scuf/datatable';
import { AxiosHttpPostFile } from 'axios-http';
import useFetch from 'helpers/hooks/useFetch';
import { RmaFilterReport, tiposRelatorio } from 'models';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { notificationAtom, RMAMANUFATURA } from 'resources';

function RmaReport() {
    const [error, setError] = useState<string>('');
    const { t } = useTranslation(['rmamanufatura', 'common']);
    const [loadingExcel, setLoadingExcel] = useState<boolean>(false);
    const [onPageChange, setOnPageChange] = useState<boolean>(false);
    const [applyFilter, setApplyFilter] = useState<boolean>(false);
    const [calendar, setCalendar] = useState<{ from: Date | null | ''; to: Date | null | '' }>({ from: null, to: null });
    const setNotification = useSetRecoilState(notificationAtom);
    const [rmaReportfilter, setRmaReportfilter] = useState<RmaFilterReport>({
        tipoRelatorio: 0,
        pageNumber: 1,
        pageSize: 10,
        isExcel: false
    });
    const [totalItems, setTotalItems] = useState<number>(0);

    const [dataTableResumo, setDataTableResumo] = useState<any[]>([]);
    const [dataTableResumoQa, setDataTableResumoQa] = useState<any[]>([]);
    const [dataTableHistorico, setDataTableHistorico] = useState<any[]>([]);
    const [relatorioResumo, setRelatorioResumo] = useState<boolean>(false);
    const [relatorioResumoQa, setRelatorioResumoQa] = useState<boolean>(false);
    const [relatorioHistorico, setRelatorioHistorico] = useState<boolean>(false);
    const [resetKey, setResetKey] = useState(0);

    const [respostaRelatorio, getRelatorios, loadingRelatorio] = useFetch<RmaFilterReport>({
        url: RMAMANUFATURA.obterRelatorio,
        method: 'POST'
    });

    const filtroInicial: RmaFilterReport = {
        tipoRelatorio: 0,
        dataInicio: null,
        dataFim: null,
        pageNumber: 1,
        pageSize: 10,
        isExcel: false
    };

    useEffect(() => {
        if (respostaRelatorio) {
            setRelatorioResumo(false);
            setRelatorioResumoQa(false);
            setRelatorioHistorico(false);
            setDataTableResumo([]);
            setDataTableResumoQa([]);
            setDataTableHistorico([]);
            if (rmaReportfilter.tipoRelatorio == 1) {
                const itens = respostaRelatorio.data.data;
                const itensMapeados = itens.map((item: any) => ({
                    id: item.id,
                    gbe: item.gbe,
                    quantity: item.quantity,
                    situation: item.situation ? 'Aprovado' : 'Não aprovado',
                    problemTypeDescription: item.problemTypeDescription ?? '-',
                    model: item.model,
                    cliente: item.cliente,
                    createdAt: item.createdAt
                }));
                setDataTableResumo(itensMapeados);
                setRelatorioResumo(true);
                setTotalItems(respostaRelatorio.data.totalCount);
            } else if (rmaReportfilter.tipoRelatorio == 2) {
                const itens = respostaRelatorio.data.data;
                const itensMapeados = itens.map((item: any) => ({
                    id: item.id,
                    gbe: item.gbe ?? '-',
                    model: item.model,
                    cliente: item.cliente,
                    createdAt: item.createdAt ?? '-',
                    dataClassificacaoObf: item.dataClassificacaoObf ?? '-',
                    obf: item.obf ? 'Sim' : 'Não',
                    semana: item.semana ?? '-',
                    quantidadeObf: item.quantidadeObf ?? '-',
                    causaRaiz: item.causaRaiz ?? '-',
                    isPedidoCorresponde: item.isPedidoCorresponde ? 'Sim' : 'Não',
                    categoria: item.categoria ?? '-'
                }));
                setDataTableResumoQa(itensMapeados);
                setRelatorioResumoQa(true);
                setTotalItems(respostaRelatorio.data.totalCount);
            } else if (rmaReportfilter.tipoRelatorio == 3) {
                const itens = respostaRelatorio.data.data;
                const itensMapeados = itens.map((item: any) => ({
                    id: item.id,
                    gbe: item.gbe ?? '-',
                    status: item.status ?? '-',
                    observacao: item.observacao ?? '-',
                    updatedAt: item.updatedAt ?? '-'
                }));
                setDataTableHistorico(itensMapeados);
                setRelatorioHistorico(true);
                setTotalItems(respostaRelatorio.data.totalCount);
            }
            if (respostaRelatorio.data.totalCount == 0) {
                setNotification(old => ({ ...old, message: 'Não há dados para mostrar.', type: 'information' }));
            } else {
                setNotification(old => ({ ...old, message: 'Relatório Gerado com sucesso!', type: 'information' }));
            }
        }
    }, [respostaRelatorio]);

    useEffect(() => {
        if (onPageChange && rmaReportfilter.tipoRelatorio) {
            ObterRelatorio();
        }
        setOnPageChange(false);
    }, [onPageChange]);

    useEffect(() => {
        if (applyFilter) {
            ObterRelatorio();
        }
        setApplyFilter(false);
    }, [applyFilter]);

    async function resetFilters() {
        setRmaReportfilter(filtroInicial);
        setResetKey(k => k + 1);
        setRelatorioResumo(false);
        setRelatorioResumoQa(false);
        setRelatorioHistorico(false);
        setDataTableResumo([]);
        setDataTableResumoQa([]);
        setDataTableHistorico([]);
    }

    async function ObterRelatorio() {
        try {
            await getRelatorios(rmaReportfilter);
        } catch {
            setNotification(old => ({ ...old, message: 'Erro ao gerar relatório', type: 'information' }));
        }
    }

    async function ObterRelatorioExcel() {
        setLoadingExcel(true);
        try {
            const resposta = await AxiosHttpPostFile({
                url: RMAMANUFATURA.obterRelatorioExcel,
                data: rmaReportfilter
            });

            if (resposta.data) {
                const url = window.URL.createObjectURL(resposta.data);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'Relatorio.xlsx';
                document.body.appendChild(link);
                link.click();
                link.remove();
                setNotification(old => ({ ...old, message: 'Relatório Exportado com sucesso!', type: 'information' }));
            } else {
                setNotification(old => ({ ...old, message: 'Erro ao exportar relatório', type: 'critical' }));
            }
        } catch {
            setNotification(old => ({ ...old, message: 'Erro ao exportar relatório', type: 'critical' }));
        }
        setLoadingExcel(false);
    }

    return (
        <div>
            <div className="form4Columns">
                <Select
                    indicator="required"
                    className="SelectStyle"
                    label={t('common:translation.Type')}
                    placeholder={t('common:translation.Type')}
                    options={tiposRelatorio}
                    value={rmaReportfilter?.tipoRelatorio}
                    onChange={value =>
                        setRmaReportfilter(old => ({
                            ...old,
                            tipoRelatorio: value
                        }))
                    }
                />

                <DatePicker
                    type="daterange"
                    key={`date-${resetKey}`}
                    label={t('common:translation.Period')}
                    rangeValue={
                        rmaReportfilter.dataInicio && rmaReportfilter.dataFim
                            ? {
                                  from: new Date(
                                      rmaReportfilter.dataInicio.getUTCFullYear(),
                                      rmaReportfilter.dataInicio.getUTCMonth(),
                                      rmaReportfilter.dataInicio.getUTCDate()
                                  ),
                                  to: new Date(
                                      rmaReportfilter.dataFim.getUTCFullYear(),
                                      rmaReportfilter.dataFim.getUTCMonth(),
                                      rmaReportfilter.dataFim.getUTCDate()
                                  )
                              }
                            : undefined
                    }
                    displayFormat={'DD/MM/YYYY'}
                    closeOnSelection={false}
                    closeOnDocumentClick={true}
                    onRangeSelect={({ from, to }: { from?: Date; to?: Date }) => {
                        if (!from || !to) {
                            setRmaReportfilter({ ...rmaReportfilter, dataInicio: null, dataFim: null });
                            return;
                        }

                        const fromStr = from.toISOString().split('T')[0];
                        const toStr = to.toISOString().split('T')[0];

                        const start = new Date(`${fromStr}T00:00:00.000Z`);
                        const end = new Date(`${toStr}T23:59:59.999Z`);

                        setRmaReportfilter({ ...rmaReportfilter, dataInicio: start, dataFim: end });
                    }}
                />
            </div>
            <div className="buttonsSearch">
                <Button
                    type="primary"
                    content={t('common:actions.ApplyFilters')}
                    onClick={() => {
                        setRmaReportfilter(old => ({ ...old, pageNumber: 1 }));
                        setApplyFilter(true);
                    }}
                    iconPosition="right"
                    loading={loadingRelatorio}
                    disabled={!rmaReportfilter?.tipoRelatorio || loadingRelatorio}
                />
                <Button
                    type="secondary"
                    icon="badge-delete"
                    content={t('common:actions.ResetFilters')}
                    onClick={() => resetFilters()}
                    iconPosition="right"
                    loading={loadingRelatorio}
                    disabled={loadingRelatorio}
                />
                <Button
                    type="primary"
                    icon="report"
                    content={t('common:actions.Generate')}
                    loading={loadingRelatorio || loadingExcel}
                    iconPosition="right"
                    disabled={
                        !rmaReportfilter?.tipoRelatorio ||
                        loadingRelatorio ||
                        loadingExcel ||
                        respostaRelatorio?.data.totalCount == 0
                    }
                    onClick={() => ObterRelatorioExcel()}
                />
            </div>

            {relatorioResumo && (
                <div className="div-datatable">
                    <DataTable data={dataTableResumo ?? []} rows={rmaReportfilter?.pageSize}>
                        <DataTable.Column field="id" header={t('RMA Id')} sortable />
                        <DataTable.Column field="situation" header={t('common:status.Situation')} />
                        <DataTable.Column field="gbe" header={t('GBE')} />
                        <DataTable.Column field="model" header={t('common:translation.Model')} />
                        <DataTable.Column field="quantity" header={t('common:translation.Quantity')} />
                        <DataTable.Column field="problemTypeDescription" header={t('common:translation.ProblemDescription')} />
                        <DataTable.Column field="cliente" header={t('common:tabs.Customer')} />
                        <DataTable.Column field="createdAt" header={t('common:translation.DateCreate')} />
                    </DataTable>
                </div>
            )}
            {relatorioResumoQa && (
                <div className="div-datatable">
                    <DataTable data={dataTableResumoQa ?? []} rows={rmaReportfilter?.pageSize}>
                        <DataTable.Column field="id" header={t('RMA Id')} sortable />
                        <DataTable.Column field="gbe" header={t('GBE')} />
                        <DataTable.Column field="model" header={t('common:translation.Model')} />
                        <DataTable.Column field="createdAt" header={t('common:translation.DateCreate')} />
                        <DataTable.Column field="cliente" header={t('common:tabs.Customer')} />
                        <DataTable.Column field="obf" header={t('OBF')} />
                        <DataTable.Column field="dataClassificacaoObf" header={t('common:translation.DateOBF')} />
                        <DataTable.Column field="semana" header={t('rmamanufatura:translation.ObfWeek')} />
                        <DataTable.Column field="quantidadeObf" header={t('common:translation.Quantity')} />
                        <DataTable.Column field="causaRaiz" header={t('rmamanufatura:translation.ObfRootCause')} />
                        <DataTable.Column field="isPedidoCorresponde" header={t('rmamanufatura:translation.ObfQuestion')} />
                        <DataTable.Column field="categoria" header={t('rmamanufatura:translation.ObfCategory')} />
                    </DataTable>
                </div>
            )}
            {relatorioHistorico && (
                <div className="div-datatable">
                    <DataTable data={dataTableHistorico ?? []} rows={rmaReportfilter?.pageSize}>
                        <DataTable.Column field="id" header={t('RMA Id')} sortable />
                        <DataTable.Column field="gbe" header={t('GBE')} />
                        <DataTable.Column field="status" header={t('common:translation.StatusRMA')} />
                        <DataTable.Column field="observacao" header={t('rmamanufatura:translation.Observation')} />
                        <DataTable.Column field="updatedAt" header={t('rmamanufatura:translation.ObfUpdate')} />
                    </DataTable>
                </div>
            )}
            {(relatorioHistorico || relatorioResumo || relatorioResumoQa) && (
                <DataTable.Pagination
                    activePage={rmaReportfilter?.pageNumber}
                    totalItems={totalItems}
                    itemsPerPage={rmaReportfilter?.pageSize}
                    disabledPages={[rmaReportfilter?.pageNumber]}
                    onPageChange={pageNumber => {
                        setOnPageChange(true);
                        setRmaReportfilter(old => ({ ...old, pageNumber: parseInt(pageNumber.toString()) }));
                    }}
                />
            )}
        </div>
    );
}

export default RmaReport;
